import { logger, getCountryByLocale } from 'utils';
import { computeExpressCheckoutRoute } from '../../utils/express-checkout';
import { getExpressRouteUrl } from '../../utils/paypal-express/getExpressRouteUrl';
import { getArticlesPresentationState } from '../../utils/articles';
import { ExpressCheckoutProceedButton } from './ExpressCheckoutProceedButton';
import { ProceedButtonSkeleton } from './ProceedButtonSkeleton';
import type { ProceedButtonAsyncProps } from './types';

/**
 * Async Server Component that displays proceed button
 *
 * Awaits articlesPromise, summaryPromise and addressesPromise to compute the
 * express checkout route server-side before rendering the button.
 * On error: logs and throws (ErrorBoundary catches and shows skeleton)
 * On no articles: logs warning and returns skeleton
 * On null/undefined summary: logs warning and returns skeleton
 *
 * Pattern: runs inside Suspense boundary to enable streaming.
 */
export async function ProceedButtonAsync(props: ProceedButtonAsyncProps) {
  const {
    articlesPromise,
    summaryPromise,
    addressesPromise,
    userId,
    routes,
    locale,
    ppExpressParam,
    constants,
    isAuthenticated
  } = props;

  const [articlesResult, summaryResult, addressesResult] = await Promise.all([
    articlesPromise,
    summaryPromise,
    addressesPromise
  ]);

  if (!articlesResult.success) {
    logger.error({
      ns: 'cart',
      type: 'ARTICLES_FETCH_ERROR',
      severity: 'high',
      msg: articlesResult.error?.message ?? 'Failed to fetch articles'
    });

    throw new Error(articlesResult.error?.message ?? 'Articles fetch error');
  }

  if (!summaryResult.success) {
    logger.error({
      ns: 'cart',
      type: 'SUMMARY_FETCH_ERROR',
      severity: 'high',
      msg: summaryResult.error?.message ?? 'Failed to fetch summary'
    });

    throw new Error(summaryResult.error?.message ?? 'Summary fetch error');
  }

  const articles = articlesResult.data;
  const summary = summaryResult.data;

  if (!articles || articles.length === 0) {
    logger.warn({
      ns: 'cart',
      msg: 'ProceedButtonAsync: No articles in cart'
    });

    return <ProceedButtonSkeleton />;
  }

  if (!summary) {
    logger.warn({
      ns: 'cart',
      msg: 'ProceedButtonAsync: Summary data is null or undefined'
    });

    return <ProceedButtonSkeleton />;
  }

  // addresses failure is non-fatal — fall back to null (routes to /cart/address)
  const addresses = addressesResult.success ? addressesResult.data : null;

  const userCountry = getCountryByLocale(locale).code;

  const expressCheckoutInfo = computeExpressCheckoutRoute(
    { articles, summary, addresses },
    { routes, userCountry, ppExpressParam }
  );

  const expressRouteUrl = getExpressRouteUrl(expressCheckoutInfo.url, ppExpressParam, summary.payment.currentPayment);

  const { inactiveArticles, inactiveChildIds } = getArticlesPresentationState(articles);

  return (
    <ExpressCheckoutProceedButton
      expressCheckoutInfo={{ ...expressCheckoutInfo, url: expressRouteUrl }}
      constants={constants}
      routes={routes}
      userId={userId}
      totalArticleCount={articles.length}
      inactiveArticles={inactiveArticles}
      inactiveChildIds={inactiveChildIds}
      isAuthenticated={isAuthenticated}
    />
  );
}
